const LOCAL_DEVELOPMENT_HOSTS = new Set([
	'localhost',
	'127.0.0.1',
	'::1',
]);

type Ipv4Octets = [number, number, number, number];

function normalizeHostname(hostname: string): string {
	const normalized = hostname.trim().toLowerCase();
	return normalized.startsWith('[') && normalized.endsWith(']')
		? normalized.slice(1, -1)
		: normalized;
}

function parseIpv4(hostname: string): Ipv4Octets | null {
	const parts = hostname.split('.');
	if (parts.length !== 4) {
		return null;
	}

	const octets = parts.map((part) => {
		if (!/^\d+$/.test(part)) return Number.NaN;
		return Number(part);
	});

	return octets.every((octet) => Number.isInteger(octet) && octet >= 0 && octet <= 255)
		? octets as Ipv4Octets
		: null;
}

function isPrivateOrLocalIpv4(hostname: string): boolean {
	const octets = parseIpv4(hostname);
	if (!octets) return false;

	const [first, second] = octets;
	return (
		first === 10 ||
		first === 127 ||
		(first === 169 && second === 254) ||
		(first === 172 && second >= 16 && second <= 31) ||
		(first === 192 && second === 168)
	);
}

function isPrivateOrLocalIpv6(hostname: string): boolean {
	const normalized = hostname.startsWith('::ffff:')
		? hostname.slice('::ffff:'.length)
		: hostname;
	const mappedIpv4 = parseIpv4(normalized);
	if (mappedIpv4) {
		return isPrivateOrLocalIpv4(normalized);
	}

	return (
		hostname === '::1' ||
		/^f[cd][0-9a-f]{0,2}:/i.test(hostname) ||
		/^fe[89ab][0-9a-f]{0,2}:/i.test(hostname)
	);
}

export function isLocalDevelopmentHost(hostname: string | null | undefined): boolean {
	if (!hostname) {
		return false;
	}

	return LOCAL_DEVELOPMENT_HOSTS.has(normalizeHostname(hostname));
}

export function isPrivateOrLocalHostname(hostname: string | null | undefined): boolean {
	if (!hostname) {
		return false;
	}

	const normalized = normalizeHostname(hostname);
	return (
		isLocalDevelopmentHost(normalized) ||
		normalized.endsWith('.local') ||
		isPrivateOrLocalIpv4(normalized) ||
		isPrivateOrLocalIpv6(normalized)
	);
}

export function hostnameMatchesRule(hostname: string, rule: string): boolean {
	const normalized = normalizeHostname(hostname);
	const normalizedRule = rule.toLowerCase();
	if (normalizedRule.startsWith('*.')) {
		return normalized.endsWith(normalizedRule.slice(1));
	}
	if (normalizedRule.startsWith('.')) {
		return normalized.endsWith(normalizedRule);
	}
	return normalized === normalizedRule;
}
